<template>
  <div class="results-listing">
    <!-- Show filter status if client-side filters are active -->
    <div v-if="hasClientSideFilters" class="filter-status">
      <div class="filter-info">
        <span class="filter-icon">🔍</span>
        <span class="filter-text">
          {{ $t('Filtering results') }}...
          {{ totalFilteredCount }} {{ $t('of') }} {{ totalCount }} {{ $t('hotels match your criteria') }}
        </span>
      </div>
    </div>

    <!-- Show skeleton loaders only when search is pending AND no results yet -->
    <template v-if="searchStore.searchPending && !hasAnyResults">
      <HotelCardSkeleton v-for="i in 5" :key="i" />
    </template>

    <!-- Show scored hotels from first page (all chunks) -->
    <template v-if="displayHotelProductsFirstPage?.length">
      <div class="result-container" v-for="(chunk, chunkIndex) in displayHotelProductsFirstPage" :key="`chunk_${chunkIndex}`">
        <template v-for="resultItemFP in chunk" :key="resultItemFP?.hotel?.id || resultItemFP?.id">
          <HotelCard
              v-if="resultItemFP?.hotel"
              :hotel="resultItemFP.hotel"
              :route="route"
              :cheapest-rate="resultItemFP.cheapest_rate"
              :price-from="resultItemFP.price_from"
          />
        </template>
      </div>
    </template>

    <!-- Show plain (unscored) hotels -->
    <template v-if="displayHotelsProducts?.length">
      <HotelCard
          v-for="resultItem in displayHotelsProducts"
          :hotel="resultItem.hotel"
          :route="route"
          :cheapest-rate="resultItem.cheapest_rate"
          :price-from="resultItem.price_from"
          :key="resultItem?.hotel?.id || resultItem?.id"
      />
    </template>

    <!-- Streaming: show skeleton cards while more results are loading -->
    <template v-if="hasAnyResults && (searchStore.searchInProgress || loadingMore)">
      <HotelCardSkeleton v-for="i in 3" :key="`stream-skel-${i}`" />
    </template>

    <!-- No results message -->
    <template v-if="!hasAnyResults && !searchStore.searchPending">
      <div class="no-results">
        <div class="no-results-icon">🏨</div>
        <div class="no-results-title">{{ $t('No hotels found') }}</div>
        <div class="no-results-message">
          {{ hasClientSideFilters ? $t('Try adjusting your filters to see more results') : $t('No hotels available for your search criteria') }}
        </div>
      </div>
    </template>

    <!-- Load more for static hotels -->
    <div v-if="!searchStore.isDynamicHotels && hasMorePages && !loadingMore" class="load-more-container">
      <button
        class="btn btn-secondary btn-load-more"
        @click="onLoadMore"
      >
        {{$t('Load more results')}}
      </button>
      <div class="load-more-count">
        {{$t('Showing')}} {{ shownCount }} {{$t('of')}} {{ searchStore.total }} {{$t('results')}}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useSearchStore } from "~/store/search";
import { useClientSideFilters } from "~/composables/useClientSideFilters";
import HotelCardSkeleton from "~/components/MarketPage/HotelCardSkeleton.vue";
import HotelCard from "~/components/MarketPage/HotelCard.vue";

const searchStore = useSearchStore();
const route = useRoute();

const ITEMS_PER_PAGE = 40
const loadedPage = ref(1)
const loadingMore = ref(false)

const hasMorePages = computed(() => {
  if (searchStore.isDynamicHotels) return false
  return shownCount.value < searchStore.total
})

const shownCount = computed(() => {
  const firstPageCount = displayHotelProductsFirstPage.value?.reduce((acc: number, chunk: any[]) => acc + chunk.length, 0) || 0
  const productsCount = displayHotelsProducts.value?.length || 0
  return firstPageCount + productsCount
})

const onLoadMore = async () => {
  loadingMore.value = true
  loadedPage.value++
  try {
    await searchStore.loadMoreStaticHotels({
      page: loadedPage.value,
      rating_gte: route.query.rating_gte,
      hotel_name: route.query.hotel_name,
      sort: route.query.sort,
    })
  } finally {
    loadingMore.value = false
  }
}

// Use client-side filtering
const {
  filteredHotelProductsFirstPage,
  filteredHotelsProducts,
  hasClientSideFilters
} = useClientSideFilters();

// Original data from store
const hotelProductsFirstPage = computed(() => searchStore.hotelProductsFirstPage);
const hotelsProducts = computed(() => searchStore.hotelsProducts);

// Use filtered data when client-side filters are active, otherwise use original data
const displayHotelProductsFirstPage = computed(() => {
  return hasClientSideFilters.value ? filteredHotelProductsFirstPage.value : hotelProductsFirstPage.value;
});

const displayHotelsProducts = computed(() => {
  return hasClientSideFilters.value ? filteredHotelsProducts.value : hotelsProducts.value;
});

// Calculate totals for filter status
const totalCount = computed(() => {
  const firstPageCount = hotelProductsFirstPage.value?.reduce((acc: number, chunk: any[]) => acc + chunk.length, 0) || 0;
  const productsCount = hotelsProducts.value?.length || 0;
  return firstPageCount + productsCount;
});

const totalFilteredCount = computed(() => {
  const firstPageCount = displayHotelProductsFirstPage.value?.reduce((acc: number, chunk: any[]) => acc + chunk.length, 0) || 0;
  const productsCount = displayHotelsProducts.value?.length || 0;
  return firstPageCount + productsCount;
});

// Check if we have any results to display
const hasAnyResults = computed(() => {
  const firstPageHasResults = displayHotelProductsFirstPage.value?.some((chunk: any[]) => chunk?.length > 0) || false;
  const productsHasResults = displayHotelsProducts.value?.length > 0 || false;
  return firstPageHasResults || productsHasResults;
});

// Reset loaded page when filters change
watch(() => [route.query.rating_gte, route.query.hotel_name, route.query.sort], () => {
  loadedPage.value = 1
})

</script>

<style scoped lang="scss">
.results-listing {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;

  @media screen and (max-width: 1024px) {
    width: 100%;
  }

  .result-container {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .filter-status {
    background-color: var(--blue-highlight-additional);
    border: 1px solid var(--blue);
    border-radius: 8px;
    padding: 12px 16px;

    .filter-info {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 14px;
      color: var(--dark-blue);

      .filter-icon {
        font-size: 16px;
      }

      .filter-text {
        font-weight: 500;
      }
    }
  }

  .no-results {
    text-align: center;
    padding: 48px 24px;
    background-color: var(--light-grey);
    border-radius: 12px;
    border: 1px dashed var(--grey-border);

    .no-results-icon {
      font-size: 48px;
      margin-bottom: 16px;
    }

    .no-results-title {
      font-size: 20px;
      font-weight: 600;
      color: var(--black);
      margin-bottom: 8px;
    }

    .no-results-message {
      font-size: 14px;
      color: var(--dark-grey);
      max-width: 400px;
      margin: 0 auto;
      line-height: 1.5;
    }
  }
}

.load-more-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 24px 0;
}

.btn-load-more {
  padding: 12px 32px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 10px;
}

.load-more-count {
  font-size: 14px;
  color: var(--dark-grey);
}

</style>
